<script lang="ts" module>
  export type SheetDetent = 'half' | 'full';

  // Open sheets, innermost last. Only the top sheet handles Esc/Tab, so a task
  // sheet opened from inside a rail sheet doesn't close (or trap focus for) both.
  const openSheets: symbol[] = [];

  /** Drag distance (px) before a press on the grabber becomes a drag, not a tap. */
  const DRAG_SLOP = 4;
  /** Downward speed (px/ms) that counts as a fling. */
  const FLING_VELOCITY = 0.5;
  /** Minimum travel (px) for a fling, so a jittery tap never dismisses. */
  const FLING_MIN_DISTANCE = 16;
  /** Upward travel (px) from `half` that expands the sheet to `full`. */
  const EXPAND_DISTANCE = 48;
</script>

<script lang="ts">
  import { useBodyScrollLock } from '../lib/useBodyScrollLock.svelte';
  import { isCoarsePointer } from '../lib/stores/viewport';
  import Icon from './Icon.svelte';

  interface Props {
    open: boolean;
    direction?: 'left' | 'right' | 'bottom';
    /** Bottom sheets only: the heights the sheet can rest at. Default `['full']`. */
    detents?: SheetDetent[];
    /** Bottom sheets only: the detent each opening starts at. Default: the first detent. */
    initialDetent?: SheetDetent;
    title?: string;
    /** Accessible name for the dialog when there is no visible `title`. */
    label?: string;
    /**
     * What receives focus on open. Default: 'container' on touch devices (so the
     * on-screen keyboard doesn't pop up over the sheet), 'first' otherwise.
     * 'first' prefers an element marked `data-sheet-autofocus`, and skips the grabber.
     */
    initialFocus?: 'container' | 'first';
    onClose: () => void;
    children: import('svelte').Snippet;
  }

  let {
    open,
    direction = 'left',
    detents = ['full'],
    initialDetent,
    title,
    label,
    initialFocus,
    onClose,
    children,
  }: Props = $props();

  let sheetEl: HTMLDivElement | undefined = $state();

  let isBottom = $derived(direction === 'bottom');
  let canToggleDetent = $derived(isBottom && detents.includes('half') && detents.includes('full'));

  // Current detent. Re-derived (back to the initial one) every time the sheet
  // opens; gestures and the grabber overwrite it while it stays open.
  let detent = $derived.by<SheetDetent>(() => {
    void open;
    return initialDetent && detents.includes(initialDetent)
      ? initialDetent
      : (detents[0] ?? 'full');
  });

  // ── Drag gesture (bottom sheets) ─────────────────────────────────────────
  interface Gesture {
    pointerId: number;
    startY: number;
    startHeight: number;
    lastY: number;
    lastTime: number;
    velocity: number; // px/ms, positive = downward
    moved: boolean;
  }
  let gesture: Gesture | null = null;
  let dragging = $state(false);
  let dragOffset = $state(0); // px, positive = pulled down, negative = pulled up
  let dragStartHeight = $state(0);
  // A drag that started on the grabber button ends with a click; don't let it toggle.
  let suppressGrabberClick = false;

  // ── On-screen keyboard (bottom sheets) ─────────────────────────────────────
  // iOS overlays the keyboard on the layout viewport instead of resizing it, so a
  // `bottom: 0` sheet ends up underneath it. iOS only scrolls a focused field into
  // view when it can (not for a field focused as the sheet opens), so the sheet
  // tracks the visual viewport itself: it stays anchored at the bottom, its white
  // running on behind the keyboard, and pads its content up above the keyboard.
  // (A slightly-off estimate then shows as extra white, never as a see-through gap.)
  let keyboardInset = $state(0); // px of the layout viewport the keyboard covers
  let visibleHeight = $state(0); // visual viewport height while the keyboard is up

  // eslint-disable-next-line momentium/no-unguarded-effect -- external subscription: visualViewport resize/scroll listeners with cleanup; writes only state it never reads
  $effect(() => {
    if (!open || !isBottom || !sheetEl || typeof window === 'undefined' || !window.visualViewport) {
      return;
    }
    const vv = window.visualViewport;
    const el = sheetEl;

    const update = () => {
      // Pinch-zoom also shrinks the visual viewport; only react at 1× (the keyboard).
      const covered = vv.scale > 1.01 ? 0 : window.innerHeight - vv.height - vv.offsetTop;
      // Locals only: reading the state written here would subscribe this effect to it.
      const inset = covered > 40 ? Math.round(covered) : 0;
      keyboardInset = inset;
      visibleHeight = inset ? Math.round(vv.height) : 0;
      const active = document.activeElement;
      if (inset && active instanceof HTMLElement && el.contains(active)) {
        active.scrollIntoView?.({ block: 'nearest' });
      }
    };

    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    // Safari's toolbar can collapse as the keyboard opens, changing innerHeight too.
    window.addEventListener('resize', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      keyboardInset = 0;
      visibleHeight = 0;
    };
  });

  // ── Closing (bottom sheets) ────────────────────────────────────────────────
  // Callers usually unmount the content together with `open` (its data is cleared
  // on close), which would collapse the panel to the grabber before it slides out.
  // Remember the open height and keep it while the panel slides away.
  let openHeight = $state(0);

  // eslint-disable-next-line momentium/no-unguarded-effect -- external subscription: ResizeObserver with cleanup; writes only state it never reads
  $effect(() => {
    if (!open || !isBottom || !sheetEl || typeof ResizeObserver === 'undefined') return;
    const el = sheetEl;
    const observer = new ResizeObserver(() => {
      openHeight = el.getBoundingClientRect().height;
    });
    observer.observe(el);
    return () => observer.disconnect();
  });

  let closingStyle = $derived(!open && isBottom && openHeight ? `height: ${openHeight}px;` : '');

  let dragStyle = $derived.by(() => {
    if (!dragging) return '';
    if (dragOffset >= 0) return `transform: translateY(${dragOffset}px);`;
    // Pulling up grows the sheet (up to its full max-height) instead of moving it.
    return canToggleDetent && detent === 'half'
      ? `max-height: min(${dragStartHeight - dragOffset}px, var(--sheet-max-h));`
      : `transform: translateY(${dragOffset / 4}px);`; // rubber band
  });

  let panelStyle = $derived(
    (keyboardInset
      ? `--sheet-keyboard: ${keyboardInset}px; --sheet-visible-h: ${visibleHeight}px;`
      : '') +
      closingStyle +
      dragStyle || undefined,
  );

  const INTERACTIVE = 'button, a[href], input, select, textarea, [contenteditable="true"]';

  function isDragHandle(target: Element): boolean {
    if (target.closest('.mobile-sheet-grabber')) return true;
    if (target.closest(INTERACTIVE)) return false;
    return !!target.closest('.mobile-sheet-header, [data-sheet-drag-handle]');
  }

  function handlePointerDown(e: PointerEvent) {
    if (!isBottom || !open || !sheetEl || !e.isPrimary || e.button > 0) return;
    if (!(e.target instanceof Element) || !isDragHandle(e.target)) return;

    const now = performance.now();
    gesture = {
      pointerId: e.pointerId,
      startY: e.clientY,
      startHeight: sheetEl.getBoundingClientRect().height || window.innerHeight,
      lastY: e.clientY,
      lastTime: now,
      velocity: 0,
      moved: false,
    };
    sheetEl.setPointerCapture?.(e.pointerId);
  }

  function handlePointerMove(e: PointerEvent) {
    if (!gesture || e.pointerId !== gesture.pointerId) return;

    const now = performance.now();
    const dt = now - gesture.lastTime;
    if (dt > 0) gesture.velocity = (e.clientY - gesture.lastY) / dt;
    gesture.lastY = e.clientY;
    gesture.lastTime = now;

    const dy = e.clientY - gesture.startY;
    if (!gesture.moved && Math.abs(dy) < DRAG_SLOP) return;
    if (!gesture.moved) {
      gesture.moved = true;
      dragStartHeight = gesture.startHeight;
      dragging = true;
    }
    dragOffset = dy;
  }

  function handlePointerUp(e: PointerEvent) {
    if (!gesture || e.pointerId !== gesture.pointerId) return;
    const { moved, startHeight } = gesture;
    // A pause before release isn't a fling.
    const velocity = performance.now() - gesture.lastTime > 100 ? 0 : gesture.velocity;
    const dy = e.clientY - gesture.startY;
    gesture = null;
    sheetEl?.releasePointerCapture?.(e.pointerId);
    dragging = false;
    dragOffset = 0;
    if (!moved) return;

    suppressGrabberClick = true;
    settle(dy, velocity, startHeight);
  }

  function handlePointerCancel(e: PointerEvent) {
    if (!gesture || e.pointerId !== gesture.pointerId) return;
    gesture = null;
    dragging = false;
    dragOffset = 0;
  }

  /** Decide where a released drag lands: another detent, back where it was, or closed. */
  function settle(dy: number, velocity: number, height: number) {
    if (dy < 0) {
      if (canToggleDetent && detent === 'half') {
        if (-dy >= EXPAND_DISTANCE || velocity <= -FLING_VELOCITY) detent = 'full';
      }
      return;
    }

    const fling = velocity >= FLING_VELOCITY && dy >= FLING_MIN_DISTANCE;
    if (canToggleDetent && detent === 'full') {
      // From full: a short pull drops to half, a long one (or hard fling) dismisses.
      if (dy > height * 0.6 || (fling && dy > height * 0.25)) onClose();
      else if (dy > height * 0.15 || fling) detent = 'half';
      return;
    }
    if (dy > height * 0.25 || fling) onClose();
  }

  function handleGrabberClick() {
    if (suppressGrabberClick) {
      suppressGrabberClick = false;
      return;
    }
    detent = detent === 'half' ? 'full' : 'half';
  }

  useBodyScrollLock(() => open);

  // Focus trap: on open, focus the first focusable element (or the panel itself on
  // touch devices, see `initialFocus`) and trap Tab cycling. On close, return focus
  // to whatever had it before (the trigger).
  // eslint-disable-next-line momentium/no-unguarded-effect -- DOM side effect: focus trap with event listener cleanup
  $effect(() => {
    if (!open || !sheetEl) return;

    const el = sheetEl;
    const token = Symbol('sheet');
    openSheets.push(token);
    const isTop = () => openSheets[openSheets.length - 1] === token;
    const returnFocusTo = document.activeElement;

    const focusFirst = (initialFocus ?? (isCoarsePointer() ? 'container' : 'first')) === 'first';

    // Focus the sheet panel itself or first focusable element
    requestAnimationFrame(() => {
      const target = focusFirst
        ? (el.querySelector<HTMLElement>('[data-sheet-autofocus]') ??
          getFocusableElements(el).find((f) => !f.classList.contains('mobile-sheet-grabber')))
        : null;
      if (target) {
        target.focus();
      } else {
        el.focus({ preventScroll: true });
      }
    });

    function handleKeydown(e: KeyboardEvent) {
      if (!isTop()) return;

      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
        return;
      }

      if (e.key === 'Tab') {
        const focusable = getFocusableElements(el);
        if (focusable.length === 0) {
          e.preventDefault();
          return;
        }

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    }

    document.addEventListener('keydown', handleKeydown);
    return () => {
      document.removeEventListener('keydown', handleKeydown);
      const index = openSheets.indexOf(token);
      if (index !== -1) openSheets.splice(index, 1);

      // Restore focus unless the user has already moved it somewhere else.
      const active = document.activeElement;
      const focusStillHere = !active || active === document.body || el.contains(active);
      if (
        focusStillHere &&
        returnFocusTo instanceof HTMLElement &&
        returnFocusTo !== document.body &&
        returnFocusTo.isConnected &&
        !el.contains(returnFocusTo)
      ) {
        returnFocusTo.focus({ preventScroll: true });
      }
    };
  });

  function getFocusableElements(container: HTMLElement): HTMLElement[] {
    const selector =
      'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';
    return Array.from(container.querySelectorAll<HTMLElement>(selector));
  }

  function handleBackdropClick() {
    onClose();
  }
</script>

<div
  class="mobile-sheet-wrapper"
  class:open
  class:direction-left={direction === 'left'}
  class:direction-right={direction === 'right'}
  class:direction-bottom={isBottom}
  aria-hidden={!open ? 'true' : undefined}
>
  <!-- Backdrop -->
  <button
    class="mobile-sheet-backdrop"
    class:visible={open}
    onclick={handleBackdropClick}
    aria-label="Close panel"
    tabindex={open ? 0 : -1}
  ></button>

  <!-- Sheet panel -->
  <div
    class="mobile-sheet-panel"
    class:open
    class:dragging
    class:keyboard-open={keyboardInset > 0}
    class:detent-half={isBottom && detent === 'half'}
    class:detent-full={isBottom && detent === 'full'}
    style={panelStyle}
    bind:this={sheetEl}
    role="dialog"
    aria-modal={open ? 'true' : undefined}
    aria-hidden={!open ? 'true' : undefined}
    aria-label={title ?? label}
    inert={!open}
    tabindex="-1"
    onpointerdown={handlePointerDown}
    onpointermove={handlePointerMove}
    onpointerup={handlePointerUp}
    onpointercancel={handlePointerCancel}
  >
    {#if isBottom}
      {#if canToggleDetent}
        <button
          type="button"
          class="mobile-sheet-grabber"
          aria-label={detent === 'half' ? 'Expand sheet' : 'Collapse sheet'}
          aria-expanded={detent === 'full'}
          onclick={handleGrabberClick}
        >
          <span class="mobile-sheet-grabber-bar"></span>
        </button>
      {:else}
        <div class="mobile-sheet-grabber" aria-hidden="true">
          <span class="mobile-sheet-grabber-bar"></span>
        </div>
      {/if}
    {/if}
    {#if title}
      <div class="mobile-sheet-header">
        <h2 class="mobile-sheet-title">{title}</h2>
        <button class="mobile-sheet-close" onclick={onClose} aria-label="Close">
          {#if isBottom}<Icon name="close" />{:else}✕{/if}
        </button>
      </div>
    {/if}
    <div class="mobile-sheet-content">
      {@render children()}
    </div>
  </div>
</div>

<style>
  .mobile-sheet-wrapper {
    position: fixed;
    inset: 0;
    z-index: var(--mobile-sheet-z, 50);
    pointer-events: none;
  }

  .mobile-sheet-wrapper.open {
    pointer-events: auto;
  }

  /* Backdrop */
  .mobile-sheet-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    border: none;
    padding: 0;
    margin: 0;
    cursor: default;
    opacity: 0;
    transition: opacity 300ms ease-out;
    pointer-events: none;
    -webkit-tap-highlight-color: transparent;
  }

  .mobile-sheet-backdrop.visible {
    opacity: 1;
    pointer-events: auto;
  }

  /* Sheet panel */
  .mobile-sheet-panel {
    position: fixed;
    top: 0;
    bottom: 0;
    width: 100%;
    background: white;
    display: flex;
    flex-direction: column;
    outline: none;
    box-shadow: 0 0 24px rgba(0, 0, 0, 0.15);
  }

  /* Direction: left */
  .direction-left .mobile-sheet-panel {
    left: 0;
    transform: translateX(-100%);
    transition: transform 200ms ease-in;
  }

  .direction-left .mobile-sheet-panel.open {
    transform: translateX(0);
    transition: transform 300ms ease-out;
  }

  /* Direction: right */
  .direction-right .mobile-sheet-panel {
    right: 0;
    transform: translateX(100%);
    transition: transform 200ms ease-in;
  }

  .direction-right .mobile-sheet-panel.open {
    transform: translateX(0);
    transition: transform 300ms ease-out;
  }

  /* Direction: bottom. Sized by its content, capped by the detent. */
  .direction-bottom .mobile-sheet-panel {
    --sheet-max-h: calc(100dvh - var(--safe-top) - 8px);
    top: auto;
    left: 0;
    right: 0;
    max-height: var(--sheet-max-h);
    padding-bottom: var(--safe-bottom);
    border-radius: 16px 16px 0 0;
    transform: translateY(calc(100% + 32px));
    transition:
      transform 200ms ease-in,
      max-height 250ms ease-out;
  }

  /* `none`, not translateY(0): a transform would make the panel the containing block
     for fixed descendants, so a sheet opened from inside this one (the backlog's
     action sheet, a task sheet in the work queue) would be laid out inside it. */
  .direction-bottom .mobile-sheet-panel.open {
    transform: none;
    transition:
      transform 300ms cubic-bezier(0.2, 0.8, 0.2, 1),
      max-height 250ms ease-out,
      padding-bottom 200ms ease-out;
  }

  .direction-bottom .mobile-sheet-panel.detent-half {
    max-height: min(55dvh, var(--sheet-max-h));
  }

  /* `data-sheet-fill` content (rails, forms with pinned actions) takes the whole
     `full` height and scrolls itself, instead of the sheet hugging it. */
  .direction-bottom
    .mobile-sheet-panel.detent-full:has(> .mobile-sheet-content > :global([data-sheet-fill])) {
    height: var(--sheet-max-h);
  }

  .direction-bottom .mobile-sheet-content:has(> :global([data-sheet-fill])) {
    display: flex;
    flex-direction: column;
  }

  /* Keyboard up (see keyboardInset): still anchored at the bottom, white behind the
     keyboard, content padded up above it. The keyboard already limits the height,
     so the half cap doesn't apply. */
  .direction-bottom .mobile-sheet-panel.keyboard-open {
    --sheet-max-h: calc(var(--sheet-visible-h) + var(--sheet-keyboard) - 8px);
    max-height: var(--sheet-max-h);
    padding-bottom: var(--sheet-keyboard);
  }

  .direction-bottom .mobile-sheet-panel.dragging {
    transition: none;
  }

  @media (prefers-reduced-motion: reduce) {
    .mobile-sheet-panel,
    .mobile-sheet-panel.open,
    .mobile-sheet-backdrop {
      transition: none !important;
    }
  }

  /* Grabber: a full-width drag strip with the visible bar centred in it */
  .mobile-sheet-grabber {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 100%;
    height: 1.5rem;
    padding: 0;
    background: none;
    border: none;
    border-radius: 16px 16px 0 0;
    cursor: grab;
    touch-action: none;
    -webkit-tap-highlight-color: transparent;
  }

  .mobile-sheet-grabber:focus-visible {
    outline: 2px solid #3b82f6;
    outline-offset: -2px;
  }

  .mobile-sheet-grabber-bar {
    width: 2.25rem;
    height: 0.3125rem;
    border-radius: 999px;
    background: #d1d5db;
  }

  .direction-bottom .mobile-sheet-header,
  .mobile-sheet-panel :global([data-sheet-drag-handle]) {
    touch-action: none;
  }

  /* Header */
  .mobile-sheet-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid #e5e7eb;
    flex-shrink: 0;
  }

  .direction-bottom .mobile-sheet-header {
    padding-top: 0;
  }

  .mobile-sheet-title {
    font-size: 0.875rem;
    font-weight: 600;
    color: #1f2937;
    margin: 0;
  }

  .mobile-sheet-close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.75rem;
    height: 1.75rem;
    font-size: 0.875rem;
    color: #6b7280;
    background: none;
    border: none;
    border-radius: 0.25rem;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }

  .mobile-sheet-close:active {
    background: #f3f4f6;
  }

  /* Bottom-sheet header standard: the sheet's own title and close button, and the
     ones a content header marks with `data-sheet-title` / `data-sheet-close`
     (rails, task sheet), all look the same. */
  .direction-bottom .mobile-sheet-title,
  .direction-bottom .mobile-sheet-panel :global([data-sheet-title]) {
    margin: 0;
    font-size: 1.0625rem;
    font-weight: 600;
    line-height: 1.3;
    color: #111827;
  }

  /* A 32px round chip with a 44px hit area (::after). */
  .direction-bottom .mobile-sheet-close,
  .direction-bottom .mobile-sheet-panel :global([data-sheet-close]) {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 2rem;
    height: 2rem;
    margin: 0;
    padding: 0;
    color: #4b5563;
    background: #f3f4f6;
    border: none;
    border-radius: 999px;
    opacity: 1;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }

  .direction-bottom .mobile-sheet-close::after,
  .direction-bottom .mobile-sheet-panel :global([data-sheet-close])::after {
    content: '';
    position: absolute;
    inset: -0.375rem;
  }

  .direction-bottom .mobile-sheet-close:active,
  .direction-bottom .mobile-sheet-panel :global([data-sheet-close]:active) {
    background: #e5e7eb;
  }

  .direction-bottom .mobile-sheet-close:focus-visible,
  .direction-bottom .mobile-sheet-panel :global([data-sheet-close]:focus-visible) {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
  }

  .direction-bottom .mobile-sheet-close :global(svg),
  .direction-bottom .mobile-sheet-panel :global([data-sheet-close] svg) {
    width: 1.125rem;
    height: 1.125rem;
    stroke-width: 2;
  }

  /* Content */
  .mobile-sheet-content {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
    display: flex;
    flex-direction: column;
  }

  /* Bottom sheets hug their content; it scrolls once the detent caps the height. */
  .direction-bottom .mobile-sheet-content {
    flex: 1 1 auto;
    min-height: 0;
    display: block;
  }

  /* Force rail content to full width inside the sheet */
  .mobile-sheet-content > :global(*) {
    width: 100% !important;
    flex: 1;
    min-height: 0;
  }
</style>
